import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { useAuth } from '../../context/AuthContext';
import { Plus, Map } from 'lucide-react';
import toast from 'react-hot-toast';

const FINANCE_ROLES = ['Super Admin', 'Secretary', 'Director'];

const Districts = () => {
  const { user } = useAuth();
  const isFinance = FINANCE_ROLES.includes(user?.role);

  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({ code: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchDistricts = async () => {
    try {
      setLoading(true);
      const response = await accountsApi.get('/districts');
      setDistricts(response.data.districts);
    } catch (err) {
      toast.error('Failed to load districts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDistricts(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.post('/districts', form);
      toast.success('District created');
      setShowCreateModal(false);
      setForm({ code: '', name: '' });
      fetchDistricts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create district');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Districts">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">Districts institutes can optionally be tagged with, for District-wise reporting.</p>
        {isFinance && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900"
          >
            <Plus size={16} /> New District
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : districts.length === 0 ? (
              <tr><td colSpan={3} className="text-center py-8 text-gray-500">No districts yet</td></tr>
            ) : districts.map(d => (
              <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-700">{d.code}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{d.name}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {d.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2"><Map size={18} /> New District</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                <input required type="text" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. KHI"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input required type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Districts;
